import { Box } from '@mui/material';
import { ILiveMachineData } from 'interfaces/maps.interface';
import { TAG_KEYS_FOR_LIVE_DATA } from 'modules/Telematics/components/MachineDataInfoWindow/constants';
import React from 'react';
import getDuration from 'utils/getDuration';
import PlaceSharpIcon from '@mui/icons-material/PlaceSharp';
import ElectricalServicesTwoToneIcon from '@mui/icons-material/ElectricalServicesTwoTone';
import SensorsTwoToneIcon from '@mui/icons-material/SensorsTwoTone';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import SpeedTwoToneIcon from '@mui/icons-material/SpeedTwoTone';
import SignalCellular1BarTwoToneIcon from '@mui/icons-material/SignalCellular1BarTwoTone';
interface Props {
	machineData: ILiveMachineData;
}
const MachineDataInfoWindow = ({ machineData }: Props) => {
	const machineId = machineData.machineId;
	const logReason = machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.LOG_REASON][0];

	const lastConnected =
		Date.now() / 1000 -
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.LAST_CONNECTED][0];
	const lastConnectedStringValue = `${getDuration(lastConnected)} ago`;
	const lat = machineData.placeGeometryResult.geometry.lat;
	const lng = machineData.placeGeometryResult.geometry.lng;
	const bearing =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.HEADING_DEGREES][0];
	const tehsil =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.TagProperty_TEHSIL][0];
	const district =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.TagProperty_DISTRICT][0];
	const state =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.TagProperty_STATE][0];
	const location = `${tehsil}, ${district}, ${state}`;
	const locationAvailable = tehsil && district && state;
	const batteryPercentage =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.BATTERY_PERCENTAGE][0];
	const batteryVoltage =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.INTERNAL_BATTERY_VOLTAGE][0];
	const batteryTemp =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.INTERNAL_TEMPERATURE][0];
	const loadedBatteryVoltage =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.LOADED_BATTERY_VOLTAGE][0];
	const batterySupply = Boolean(
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.SUPPLY_GOOD][0],
	);
	const externalPowerConnected = Boolean(
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.EXTERNAL_POWER_CONNECTED][0],
	);
	const externalVoltage =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.EXTERNAL_VOLTAGE][0];
	const ignitionOn = Boolean(
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.IGNITION_ON][0],
	);
	const engineOn = Boolean(
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.ENGINE_ON][0],
	);
	const sprayerOn = Boolean(
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.SPRAYER_ON][0],
	);
	const speed = machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.SPEED][0];
	const odometer = machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.ODOMETER][0];
	const cellular =
		machineData.tagJson[TAG_KEYS_FOR_LIVE_DATA.CELLULAR_SIGNAL_STRENGTH][0];
	return (
		<Box
			sx={{
				width: '400px ',
				border: '1px soild lightgrey',
				borderRadius: '5px',
			}}
		>
			<Box
				id="header"
				sx={{
					display: 'flex',
					background: 'lightgrey',
					height: '40px',
					alignItems: 'center',
				}}
			>
				<Box sx={{ marginLeft: '10px' }} id="icon">
					<img
						src={machineData?.machineIcon?.url}
						alt={machineData?.machineId}
						title={machineData?.machineIcon?.desc ?? ''}
						height="25px"
						width="25px"
					/>
				</Box>
				<Box sx={{ marginLeft: '10px' }} id="machine-id">
					{machineId}
				</Box>
			</Box>
			<Box
				sx={{
					padding: '10px',
					'&>div': {
						border: '1px solid lightgrey',
						borderRadius: '5px',
						marginTop: '10px',
						padding: '10px',
						alignItems: 'center',
					},
				}}
				id="content"
			>
				<Box>
					{logReason}, {lastConnectedStringValue}
				</Box>
				<Box id="location">
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							flexWrap: 'wrap',
						}}
					>
						<PlaceSharpIcon />
						<Box marginLeft="5px">
							{lat} , {lng}
						</Box>
						<Box marginLeft="5px">accurate by 5 mts</Box>
					</Box>
					<Box>Bearing : {bearing} deg C</Box>
					{locationAvailable && <Box>Located at: {location}</Box>}
				</Box>
				<Box id="power">
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						Power <ElectricalServicesTwoToneIcon />
						<Box marginLeft="5px">{batteryPercentage}%</Box>
					</Box>
					<Box
						sx={{
							display: 'flex',
							marginTop: '5px',
						}}
					>
						<Box>Battery: {batteryVoltage}V</Box>
						<Box marginLeft="10px">Temp: {batteryTemp} deg C</Box>
					</Box>
					<Box
						sx={{
							display: 'flex',
						}}
					>
						<Box>Load: {loadedBatteryVoltage}V</Box>
						<Box marginLeft="10px">
							Supply Good: {batterySupply ? 'True' : 'False'}
						</Box>
					</Box>
					<Box
						sx={{
							display: 'flex',
						}}
					>
						<Box>
							Ext Power: {externalPowerConnected ? 'On' : 'Off'}
						</Box>
						<Box marginLeft="10px">Voltage: {externalVoltage}V</Box>
					</Box>
				</Box>
				<Box id="sensors">
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						Sensors
						<SensorsTwoToneIcon sx={{ marginLeft: '5px' }} />
					</Box>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch size="small" checked={ignitionOn} />
							}
							label="Ignition On"
						/>
					</FormGroup>
					<FormGroup>
						<FormControlLabel
							control={<Switch size="small" checked={engineOn} />}
							label="Engine On"
						/>
					</FormGroup>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch size="small" checked={sprayerOn} />
							}
							label="Sprayer On"
						/>
					</FormGroup>
				</Box>
				<Box id="movement">
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						Movement <SpeedTwoToneIcon sx={{ marginLeft: '5px' }} />
					</Box>
					<Box display="flex" alignItems="center">
						<Box>Speed: {speed} kmph</Box>
						<Box marginLeft="5px">Odo: {odometer} kms</Box>
					</Box>
				</Box>
				<Box id="cellular-strength">
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						Cellular strength{' '}
						<SignalCellular1BarTwoToneIcon
							sx={{ marginLeft: '5px' }}
						/>
						: {cellular}%
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
export default MachineDataInfoWindow;
